import React, { useEffect, useState, useMemo } from 'react';
import {
    Dialog,
    DialogContent,
    IconButton,
    Stack,
    Typography,
    Button,
    Box,
    CircularProgress,
    Snackbar,
    Alert
} from '@mui/material';
import {
    Close as CloseIcon,
    Download as DownloadIcon,
    OpenInNew as OpenInNewIcon,
    Email as EmailIcon
} from '@mui/icons-material';

// Detect iOS/iPadOS
const isIOS = () => {
    return /iPad|iPhone|iPod/.test(navigator.userAgent) ||
        (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
};

const imageExts = ['png', 'jpg', 'jpeg', 'tif', 'tiff', 'gif', 'bmp', 'webp'];
const getFileExt = (name) => (name || '').split('.').pop()?.toLowerCase() || '';

/**
 * Modal for previewing PDF and images inside the app
 * On iOS: shows buttons to open/download (iframe doesn't work well for PDF)
 * On Desktop: shows PDF in iframe, images as <img>
 */
export default function PdfPreviewModal({ open, onClose, pdfBlob, filename = 'JHA.pdf', loading = false, onEmailToMe = null }) {
    const [pdfUrl, setPdfUrl] = useState(null);
    const [error, setError] = useState(null);
    const [emailSending, setEmailSending] = useState(false);
    const [emailMessage, setEmailMessage] = useState(null); // { type: 'success'|'error', text: '...' }
    const isiOSDevice = useMemo(() => isIOS(), []);
    const ext = useMemo(() => getFileExt(filename), [filename]);
    const isImage = useMemo(() => imageExts.includes(ext), [ext]);
    const previewTitle = isImage ? 'Image Preview' : 'PDF Preview';

    // Reset email feedback whenever modal opens for a new file
    useEffect(() => {
        if (open) setEmailMessage(null);
    }, [open, filename]);

    const handleEmailToMe = async () => {
        if (!onEmailToMe || emailSending) return;
        setEmailSending(true);
        setEmailMessage(null);
        try {
            const result = await onEmailToMe();
            setEmailMessage({ type: 'success', text: (result && result.message) || 'Email sent.' });
        } catch (err) {
            const msg = err?.response?.data?.message
                || (err?.response?.status === 429 ? 'Too many requests. Try again later.' : 'Failed to send email.');
            setEmailMessage({ type: 'error', text: msg });
        } finally {
            setEmailSending(false);
        }
    };

    // Create object URL when blob changes
    useEffect(() => {
        if (pdfBlob) {
            try {
                const url = URL.createObjectURL(pdfBlob);
                setPdfUrl(url);
                setError(null);
            } catch (e) {
                console.error('Failed to create PDF URL:', e);
                setError('Failed to load PDF preview');
            }
        }

        // Cleanup on unmount or when blob changes
        return () => {
            if (pdfUrl) {
                URL.revokeObjectURL(pdfUrl);
            }
        };
    }, [pdfBlob]);

    // Cleanup when modal closes
    useEffect(() => {
        if (!open && pdfUrl) {
            URL.revokeObjectURL(pdfUrl);
            setPdfUrl(null);
        }
    }, [open]);

    const handleDownload = () => {
        if (!pdfBlob) return;

        try {
            const url = URL.createObjectURL(pdfBlob);
            const a = document.createElement('a');
            a.href = url;
            a.download = filename;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            setTimeout(() => URL.revokeObjectURL(url), 100);
        } catch (e) {
            console.error('Failed to download PDF:', e);
            alert('Failed to download PDF. Please try again.');
        }
    };

    const handleOpenInBrowser = () => {
        if (!pdfUrl) return;
        // Open in new tab - will leave PWA but allows proper PDF viewing on iOS
        window.open(pdfUrl, '_blank');
    };

    const handleClose = () => {
        if (pdfUrl) {
            URL.revokeObjectURL(pdfUrl);
            setPdfUrl(null);
        }
        onClose();
    };

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            maxWidth={false}
            fullScreen
            PaperProps={{
                sx: {
                    backgroundColor: '#1a1a1a',
                    display: 'flex',
                    flexDirection: 'column'
                }
            }}
        >
            {/* Header */}
            <Box
                sx={{
                    px: 2,
                    py: 1.5,
                    backgroundColor: '#2d2d2d',
                    borderBottom: '1px solid #444',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    flexShrink: 0,
                    zIndex: 10
                }}
            >
                <Typography variant="subtitle1" sx={{ color: '#fff', fontWeight: 600 }}>
                    {previewTitle}
                </Typography>

                <Stack direction="row" spacing={1} alignItems="center">
                    {isiOSDevice && pdfUrl && !loading && (
                        <Button
                            variant="contained"
                            size="small"
                            startIcon={<OpenInNewIcon />}
                            onClick={handleOpenInBrowser}
                            sx={{
                                backgroundColor: '#4caf50',
                                '&:hover': { backgroundColor: '#43a047' },
                                textTransform: 'none'
                            }}
                        >
                            Open
                        </Button>
                    )}
                    {onEmailToMe && (
                        <Button
                            variant="contained"
                            size="small"
                            startIcon={<EmailIcon />}
                            onClick={handleEmailToMe}
                            disabled={!pdfBlob || loading || emailSending}
                            sx={{
                                backgroundColor: '#7b1fa2',
                                '&:hover': { backgroundColor: '#6a1b9a' },
                                textTransform: 'none'
                            }}
                        >
                            {emailSending ? 'Sending…' : 'Email to me'}
                        </Button>
                    )}
                    <Button
                        variant="contained"
                        size="small"
                        startIcon={<DownloadIcon />}
                        onClick={handleDownload}
                        disabled={!pdfBlob || loading}
                        sx={{
                            backgroundColor: '#1976d2',
                            '&:hover': { backgroundColor: '#1565c0' },
                            textTransform: 'none'
                        }}
                    >
                        Download
                    </Button>
                    <IconButton
                        onClick={handleClose}
                        size="medium"
                        sx={{
                            color: '#fff',
                            backgroundColor: 'rgba(255,255,255,0.1)',
                            '&:hover': { backgroundColor: 'rgba(255,255,255,0.2)' },
                            ml: 1
                        }}
                    >
                        <CloseIcon />
                    </IconButton>
                </Stack>
            </Box>

            {/* Content */}
            <DialogContent
                sx={{
                    flex: 1,
                    p: 0,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    overflow: 'auto',
                    backgroundColor: '#525252'
                }}
            >
                {loading ? (
                    <Stack alignItems="center" spacing={2}>
                        <CircularProgress sx={{ color: '#fff' }} size={48} />
                        <Typography sx={{ color: '#ccc', fontSize: '1.1rem' }}>
                            Generating PDF...
                        </Typography>
                    </Stack>
                ) : error ? (
                    <Stack alignItems="center" spacing={2}>
                        <Typography sx={{ color: '#f44336' }}>
                            {error}
                        </Typography>
                        <Button
                            variant="outlined"
                            onClick={handleClose}
                            sx={{ color: '#fff', borderColor: '#fff' }}
                        >
                            Close
                        </Button>
                    </Stack>
                ) : pdfUrl ? (
                    isImage ? (
                        // Image preview
                        <Box
                            component="img"
                            src={pdfUrl}
                            alt={filename}
                            sx={{
                                maxWidth: '100%',
                                maxHeight: '100%',
                                objectFit: 'contain',
                            }}
                        />
                    ) : isiOSDevice ? (
                        // iOS: Show message with buttons (iframe doesn't work well)
                        <Stack alignItems="center" spacing={3} sx={{ p: 3, textAlign: 'center' }}>
                            <Box
                                sx={{
                                    width: 80,
                                    height: 80,
                                    borderRadius: 2,
                                    backgroundColor: '#d32f2f',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center'
                                }}
                            >
                                <Typography sx={{ color: '#fff', fontSize: '2rem', fontWeight: 700 }}>
                                    PDF
                                </Typography>
                            </Box>
                            <Typography sx={{ color: '#fff', fontSize: '1.2rem', fontWeight: 600 }}>
                                PDF Ready
                            </Typography>
                            <Typography sx={{ color: '#bbb', maxWidth: 300 }}>
                                Tap "Open" to view the PDF in Safari, or "Download" to save it to your device.
                            </Typography>
                            <Stack direction="row" spacing={2} sx={{ mt: 2 }}>
                                <Button
                                    variant="contained"
                                    size="large"
                                    startIcon={<OpenInNewIcon />}
                                    onClick={handleOpenInBrowser}
                                    sx={{
                                        backgroundColor: '#4caf50',
                                        '&:hover': { backgroundColor: '#43a047' },
                                        textTransform: 'none',
                                        px: 3
                                    }}
                                >
                                    Open in Safari
                                </Button>
                                <Button
                                    variant="contained"
                                    size="large"
                                    startIcon={<DownloadIcon />}
                                    onClick={handleDownload}
                                    sx={{
                                        backgroundColor: '#1976d2',
                                        '&:hover': { backgroundColor: '#1565c0' },
                                        textTransform: 'none',
                                        px: 3
                                    }}
                                >
                                    Download
                                </Button>
                            </Stack>
                        </Stack>
                    ) : (
                        // Desktop: Show PDF in iframe
                        <Box
                            component="iframe"
                            src={pdfUrl}
                            sx={{
                                width: '100%',
                                height: '100%',
                                border: 'none',
                                backgroundColor: '#fff'
                            }}
                            title="PDF Preview"
                        />
                    )
                ) : (
                    <Typography sx={{ color: '#999' }}>
                        No file to display
                    </Typography>
                )}
            </DialogContent>

            <Snackbar
                open={!!emailMessage}
                autoHideDuration={emailMessage?.type === 'success' ? 4000 : 6000}
                onClose={() => setEmailMessage(null)}
                anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
                sx={{ mt: 8 }}
            >
                {emailMessage ? (
                    <Alert
                        onClose={() => setEmailMessage(null)}
                        severity={emailMessage.type}
                        variant="filled"
                        sx={{ width: '100%' }}
                    >
                        {emailMessage.text}
                    </Alert>
                ) : undefined}
            </Snackbar>
        </Dialog>
    );
}
